import Highchart from '@features/reports/components/charts/charts/Highcharts'
import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import type { ChartDataPoint } from '../types'
import { getActionTitle } from '../utils/stockRecordUtils'

type MedStockChartProps = {
  medication: {
    stockQuantityType?: string
    name?: string
  }
  records: any[] // Not needed if chartData includes record
  chartData: ChartDataPoint[]
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  testId?: string
}

/**
 * Simplified version that uses backend-generated chartData directly
 * Requires backend to include: marker config, record data, correct stockLevel
 */
const MedStockChart = ({
  medication,
  records,
  chartData,
  startDate,
  endDate,
  testId
}: MedStockChartProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { stockQuantityType } = medication
  const showExpectedStock = true

  // Create a map of records by ID for matching with chartData
  const recordsById = new Map<string, any>()
  records.forEach((record) => {
    recordsById.set(record.id, record)
  })

  // Filter out expected stock points (they're for visual gaps, not main line)
  // Also filter out administration records (type === 'administration')
  // Map chartData directly to Highcharts format
  const data = chartData
    .filter((point) => {
      // Skip expected stock visualization points
      if (point.expectedStockLevel) return false

      // Filter out administration records
      const fullRecord = point.recordId ? recordsById.get(point.recordId) : null
      const record = fullRecord || point.record
      if (record?.type === 'administration') return false

      return true
    })
    .map((point) => {
      // Match chartData record with full record from records array for populated fields
      const fullRecord = point.recordId ? recordsById.get(point.recordId) : null
      const record = fullRecord || point.record

      // Convert backend 'reason' to 'action' for compatibility
      const action = record.action || record.reason
      const isStockCount =
        action === 'STOCK_COUNT' || point.reason === 'STOCK_COUNT'

      // Check for unresolved discrepancy (matching original logic)
      const hasUnresolvedDiscrepancy =
        (record.hasDiscrepancy || !!record.discrepancy) &&
        record.discrepancy?.status !== 'RECONCILED'

      const hasActualStock = typeof record.stockCount === 'number'
      const hasExpectedStock =
        typeof record.expectedStock === 'number' ||
        typeof record.previousStockLevel === 'number'

      const hasStockChanged =
        isStockCount &&
        hasActualStock &&
        hasExpectedStock &&
        record.stockCount !==
          (record.expectedStock ?? record.previousStockLevel) &&
        hasUnresolvedDiscrepancy

      // Use marker from backend if available, otherwise determine based on original logic
      let marker = point.marker
      if (!marker && showExpectedStock) {
        // Show alert icon for STOCK_COUNT with unresolved discrepancy, tick for others
        marker = {
          symbol: hasStockChanged
            ? 'https://lmc-data-production.s3.eu-west-2.amazonaws.com/public/alert-icon2.png'
            : 'https://lmc-data-production.s3.eu-west-2.amazonaws.com/public/tick-icon.png',
          height: 18,
          width: 18,
          color: hasStockChanged ? colors.yellow : colors.blue,
          lineColor: hasStockChanged ? colors.yellow : colors.blue
        }
      }

      return {
        x: dayjs(point.date).valueOf(),
        y: point.stockLevel,
        record: {
          ...record,
          action
        },
        color: marker?.color || colors.blue,
        marker:
          marker && showExpectedStock
            ? {
                enabled: true,
                symbol: marker.symbol?.startsWith('url(')
                  ? marker.symbol
                  : `url(${marker.symbol})`, // Ensure url() wrapper for Highcharts
                height: marker.height,
                width: marker.width,
                lineColor: marker.lineColor
              }
            : undefined // No marker if showExpectedStock is false
      }
    })
    .sort((a, b) =>
      dayjs(a.record.date || a.record.actionTime).diff(
        dayjs(b.record.date || b.record.actionTime)
      )
    )

  return (
    <div data-testid={testId ? `${testId}-med-stock-chart` : 'med-stock-chart'}>
      <Highchart
        config={{
          yAxisLabel: `Stock Count ${
            stockQuantityType ? `(${stockQuantityType})` : ''
          }`,
          from: startDate,
          minX: dayjs(startDate).endOf('day').valueOf(),
          maxX: dayjs(endDate).endOf('day').valueOf(),
          tooltip: {
            formatter: function (this: any) {
              const { record } = this.point
              if (!record) return ''

              const recordedByName =
                typeof record.recordedBy?.name === 'string'
                  ? record.recordedBy.name
                  : [
                      record.recordedBy?.name?.first,
                      record.recordedBy?.name?.last
                    ]
                      .filter(Boolean)
                      .join(' ') || ''

              const notes = Array.isArray(record.notes)
                ? record.notes.join(', ')
                : record.notes || ''

              // Use action field (we set it in the mapping above)
              const actionTitle = getActionTitle(
                record.action,
                t,
                record.administrationStatus
              )

              const discrepancyReason =
                record.discrepancy?.reason ||
                record.discrepancy?.carerReason ||
                ''
              const isResolved = record.discrepancy?.status === 'RECONCILED'

              // For STOCK_COUNT, show stockCount if available, otherwise use resultingStockLevel
              const stockCount =
                typeof record.stockCount === 'number'
                  ? record.stockCount
                  : record.action === 'STOCK_COUNT' ||
                      record.reason === 'STOCK_COUNT'
                    ? record.resultingStockLevel
                    : null

              // Expected stock: use expectedStock if available, otherwise previousStockLevel
              const expectedStock =
                typeof record.expectedStock === 'number'
                  ? record.expectedStock
                  : record.previousStockLevel

              return `<strong style="font-size: 12px; opacity: 0.6;">
                                    ${dayjs(this.x).format('ddd Do MMM - HH:mm')} - ${recordedByName}
                                </strong>
                                <br />
                                <b style="font-size: 16px; font-weight: 600;">${actionTitle}</b><br />
                                ${notes}<br />
                                ${
                                  stockCount !== null &&
                                  stockCount !== undefined
                                    ? `<b style="font-size: 10px; opacity: 0.6;">${
                                        stockCount
                                      } ${
                                        stockQuantityType || ''
                                      } counted</b><br />`
                                    : ''
                                }
                                ${
                                  expectedStock !== null &&
                                  expectedStock !== undefined
                                    ? `<b style="font-size: 10px; opacity: 0.6;">${
                                        expectedStock
                                      } ${
                                        stockQuantityType || ''
                                      } expected</b><br />`
                                    : ''
                                }
                                ${
                                  discrepancyReason
                                    ? `<br /><b style="font-size: 10px; color: ${
                                        isResolved
                                          ? colors.successgreen
                                          : colors.yellow
                                      };">${
                                        isResolved
                                          ? '✓ Resolved: '
                                          : 'Discrepancy: '
                                      }${discrepancyReason}</b>`
                                    : ''
                                }`
            },
            style: { width: '300px' }
          },
          series: [
            {
              name: 'Stock',
              data: data,
              color: colors.blue,
              // Don't override point-level markers
              marker: {
                enabled: true,
                lineColor: colors.blue,
                fillColor: 'white'
              }
            }
          ]
        }}
      />
    </div>
  )
}

export default MedStockChart
